"use client";

import {useTranslation} from "react-i18next";
import {humanizeToolField} from "@repo/ui/gailgpt/tool-presentation";
import ProposalFieldLabel from "./ProposalFieldLabel";
import {actionFieldLabel} from "./useCaseSnapshot";
import {occurrenceKeys, valueKey} from "./snapshotKeys";
import ApprovalReferenceValue, {useApprovalReferences} from "./ApprovalReferenceValue";
import {approvalReferenceKind, type ApprovalValuePath} from "./approvalReferences";

/** Known enum values get labels; anything else is shown exactly as the API sent it. */
const VALUE_LABELS: Record<string, Record<string, string>> = {
   type: {default: "typeDefault", text_message: "typeTextMessage"},
   status: {active: "statusActive", archived: "statusArchived"},
};

interface FrozenValueProps {
   value: unknown;
   /** The property holding this value, which decides how it is labelled. */
   field?: string;
   path?: ApprovalValuePath;
   scriptNames?: ReadonlyMap<string, string>;
   humanizeLabels?: boolean;
}

/** Render API values without normalization. Array position is part of the proposal. */
export default function FrozenValue({value, field, path, scriptNames, humanizeLabels = false}: FrozenValueProps) {
   const {t} = useTranslation("ask");
   const valuePath: ApprovalValuePath = path ?? {source: "snapshot", segments: field ? [field] : []};
   const muted = (key: string) => <span className="italic text-500">{t(`toolApproval.${key}`)}</span>;
   if (value === undefined) return muted("absentValue");
   if (value === null) return muted("nullValue");
   if (value === "") return muted("emptyValue");
   if (typeof value === "boolean") return <>{t(`toolApproval.${value ? "trueValue" : "falseValue"}`)}</>;
   if (Array.isArray(value)) return value.length ? <ol className="list-decimal space-y-3 pl-5 marker:font-semibold marker:text-500">
      {occurrenceKeys(value, valueKey).map(({key, item}, index) => <li key={key} className="pl-1">
         <FrozenValue value={item} field={field} path={{...valuePath, segments: [...valuePath.segments, String(index)]}} scriptNames={scriptNames} humanizeLabels={humanizeLabels} />
      </li>)}
   </ol> : muted("emptyList");
   if (typeof value === "object" && !Object.keys(value).length) return muted("emptyValue");
   if (typeof value === "object") return <FrozenObject value={value} field={field} path={valuePath} scriptNames={scriptNames} humanizeLabels={humanizeLabels} />;
   return <FrozenScalar value={value} field={field} path={valuePath} scriptNames={scriptNames} />;
}

function FrozenObject({value, field, path, scriptNames, humanizeLabels}: FrozenValueProps & {value: object; path: ApprovalValuePath}) {
   const {t, i18n} = useTranslation("ask");
   return <dl className="space-y-2">{Object.entries(value).map(([key, item]) => {
      const translated = `toolApproval.fields.${key}`;
      const humanizedLabel = i18n.exists(translated, {ns: "ask"}) ? t(translated) : humanizeToolField(key);
      return <div key={key}>
         <dt className="text-xs font-medium text-500">{humanizeLabels
            ? humanizedLabel
            : <ProposalFieldLabel field={field ? key : actionFieldLabel(key)} />}</dt>
         <dd className="whitespace-pre-wrap [overflow-wrap:anywhere]"><FrozenValue value={item} field={key} path={{...path, segments: [...path.segments, key]}} scriptNames={scriptNames} humanizeLabels={humanizeLabels} /></dd>
      </div>;
   })}</dl>;
}

function FrozenScalar({value, field, path, scriptNames}: FrozenValueProps & {path: ApprovalValuePath}) {
   const {t} = useTranslation("ask");
   const {toolName} = useApprovalReferences();
   const text = String(value);
   const kind = approvalReferenceKind(path.segments, path.source === "request" ? toolName : undefined);
   const labelKey = field ? VALUE_LABELS[field]?.[text] : undefined;
   if (typeof value === "string") {
      let name: string | undefined;
      if (kind === "textScript") name = scriptNames?.get(text.toLowerCase());
      else if (labelKey) name = t(`toolApproval.${labelKey}`);
      return <ApprovalReferenceValue id={value} kind={kind} path={path} name={name} />;
   }
   if (labelKey) return <>{t(`toolApproval.${labelKey}`)}</>;
   return <span className="whitespace-pre-wrap [overflow-wrap:anywhere]">{text}</span>;
}
